import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Sparkles, X, Send, Loader2, AlertTriangle, ArrowRight, Check, Ban, ShieldCheck } from "lucide-react";
import { hasRole, type CoretelUser } from "@/lib/auth";
import { apiFetch } from "@/lib/supabase";
import { extractRoutes } from "@/lib/appRoutes";

// =============================================================================
// AskCoretel — T-447 Stage 3. Global "Ask Coretel" launcher + slide-over chat.
//
// Manager+ only. Sends questions to the /ai/assist Worker endpoint (RLS-scoped
// tools). The current route is passed as a context hint. The assistant can
// only see what the user's account is allowed to see. Stage B (mig 340): it
// can PROPOSE a few changes; each arrives as a card and the user's Confirm is
// what applies it - under their own session, via the worker.
//
// NOTE: each question is answered with a fresh model context (the thread_id
// keeps the audit trail together, but prior turns are not yet replayed into the
// model). Multi-turn memory is a near-term enhancement.
// =============================================================================

// Stage B (mig 340) - a proposed change rides on the assistant message as a
// card. Nothing happens until the user taps Confirm; the worker executes it
// under their own session and reports back.
type ActionCard = {
  action_id: string; summary: string; action_type: string;
  state: "proposed" | "working" | "executed" | "cancelled" | "failed"; error?: string;
};
type Msg = { role: "user" | "assistant"; text: string; pending?: boolean; actions?: ActionCard[] };

const ACTION_LABEL: Record<string, string> = {
  project_status: "Project status", close_defect: "Close defect",
  move_booking: "Move booking", move_stint: "Move stint",
};

const SUGGESTIONS_MANAGER = [
  "How do I raise an RFQ?",
  "Which projects are live right now?",
  "How do I approve timesheets?",
];
const SUGGESTIONS_DIRECTOR = [
  "Which projects are live right now?",
  "How is a project's margin calculated?",
  "How do I record a pay run?",
];

export function AskCoretel({ user }: { user: CoretelUser }) {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [threadId, setThreadId] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Manager+ only (defensive — AppShell also gates the mount).
  if (!hasRole(user, "manager")) return null;

  const suggestions = hasRole(user, "director") ? SUGGESTIONS_DIRECTOR : SUGGESTIONS_MANAGER;

  function replaceLastPending(list: Msg[], text: string, actions?: ActionCard[]): Msg[] {
    const copy = list.slice();
    for (let i = copy.length - 1; i >= 0; i--) {
      if (copy[i].pending) { copy[i] = { role: "assistant", text, actions }; break; }
    }
    return copy;
  }

  function setCard(actionId: string, patch: Partial<ActionCard>) {
    setMsgs(m => m.map(msg => !msg.actions ? msg : ({
      ...msg, actions: msg.actions.map(a => a.action_id === actionId ? { ...a, ...patch } : a),
    })));
  }

  // Confirm = the worker runs the change under YOUR session (same rules as
  // the button in the app), then we refresh whatever screens show it.
  async function decide(card: ActionCard, verb: "confirm" | "cancel") {
    setCard(card.action_id, { state: "working" });
    try {
      const res = await apiFetch(`/ai/assist/actions/${card.action_id}/${verb}`, { method: "POST" });
      const data: any = await res.json().catch(() => ({}));
      if (!res.ok) {
        setCard(card.action_id, { state: "failed", error: String(data?.error ?? data?.detail ?? `Request failed (${res.status})`) });
        return;
      }
      setCard(card.action_id, { state: verb === "cancel" ? "cancelled" : "executed" });
      if (verb === "confirm") {
        // Whatever the change touched, make the app show it without a reload.
        for (const key of ["schedule_pcas", "schedule_roster", "project", "projects", "defects", "site_defects", "visit_roster"]) {
          qc.invalidateQueries({ queryKey: [key] });
        }
        qc.invalidateQueries();
      }
    } catch (e: any) {
      setCard(card.action_id, { state: "failed", error: String(e?.message ?? e) });
    }
  }

  async function send(q: string) {
    const question = q.trim();
    if (!question || busy) return;
    setErr(null);
    setInput("");
    setMsgs(m => [...m, { role: "user", text: question }, { role: "assistant", text: "", pending: true }]);
    setBusy(true);
    try {
      const res = await apiFetch("/ai/assist", {
        method: "POST",
        body: JSON.stringify({ question, thread_id: threadId, context_route: location.pathname }),
      });
      const data: any = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data?.detail || data?.error || ("Request failed (" + res.status + ")");
        setMsgs(m => replaceLastPending(m, "Sorry — I hit an error: " + detail));
        setErr(String(detail));
      } else {
        if (data.thread_id) setThreadId(data.thread_id);
        const cards: ActionCard[] | undefined = Array.isArray(data.actions) && data.actions.length
          ? data.actions.map((a: any) => ({ action_id: a.action_id, summary: a.summary, action_type: a.action_type, state: "proposed" as const }))
          : undefined;
        setMsgs(m => replaceLastPending(m, String(data.answer ?? "(no answer)"), cards));
      }
    } catch (e: any) {
      setMsgs(m => replaceLastPending(m, "Sorry — I couldn't reach the assistant. Check your connection and try again."));
      setErr(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  function newChat() {
    setMsgs([]); setThreadId(undefined); setErr(null); setInput("");
  }

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed z-40 bottom-20 right-4 md:bottom-6 md:right-6 flex items-center gap-2 rounded-full bg-accent text-white shadow-lg px-4 py-3 hover:opacity-90 transition"
          title="Ask Coretel — how-to help and your data"
          type="button"
        >
          <Sparkles size={18} />
          <span className="text-[13px] font-medium hidden sm:inline">Ask Coretel</span>
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          {/* 22 Oct 2026 mobile audit — James: "Ask Coretel can take over the
              screen; cannot easily exit because the close sits too high".
              On a phone it is a bottom sheet (88dvh, rounded top, the page
              shows behind it and a tap there closes it), the header clears
              the notch, Close is a labelled 44px button, and the composer
              sits above the home indicator. Desktop keeps the side panel. */}
          <div className="relative w-full sm:w-[420px] h-[100dvh] max-md:h-[88dvh] max-md:mt-auto max-md:rounded-t-2xl bg-ink-raised border-l max-md:border-l-0 max-md:border-t border-line flex flex-col shadow-2xl">
            <div className="md:hidden flex justify-center pt-2"><span className="w-10 h-1 rounded-full bg-line" /></div>
            <div className="flex items-center justify-between px-4 py-3 border-b border-line" style={{ paddingTop: "max(0.75rem, var(--safe-top, 0px))" }}>
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-accent" />
                <span className="text-[14px] font-semibold text-text">Ask Coretel</span>
              </div>
              <div className="flex items-center gap-1">
                {msgs.length > 0 && (
                  <button onClick={newChat} className="text-[11px] text-text-muted hover:text-text px-2 py-1 rounded" type="button">New chat</button>
                )}
                <button onClick={() => setOpen(false)} className="text-text hover:text-accent min-h-[44px] min-w-[44px] px-2 rounded-lg inline-flex items-center justify-center gap-1 text-[13px] font-medium border border-line max-md:bg-ink-card" title="Close" type="button"><X size={18} /><span className="md:hidden">Close</span></button>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {msgs.length === 0 ? (
                <div className="text-[13px] text-text-muted">
                  <p className="mb-1 text-text">Hi — I can help with how-to questions and your live project data.</p>
                  <p className="mb-3 text-text-dim text-[12px]">I only see what your account is allowed to see. I can propose a few changes — project status, closing a defect, moving a booking — and nothing happens until you tap Confirm.</p>
                  <div className="space-y-1.5">
                    {suggestions.map(s => (
                      <button key={s} onClick={() => send(s)} className="block w-full text-left text-[12px] px-3 py-2 rounded border border-line hover:bg-ink-hover text-text" type="button">{s}</button>
                    ))}
                  </div>
                </div>
              ) : (
                msgs.map((m, i) => (
                  <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                    <div className={
                      m.role === "user"
                        ? "max-w-[85%] rounded-2xl rounded-br-sm bg-accent text-white px-3 py-2 text-[13px] whitespace-pre-wrap"
                        : "max-w-[90%] rounded-2xl rounded-bl-sm bg-ink-card border border-line px-3 py-2 text-[13px] text-text whitespace-pre-wrap"
                    }>
                      {m.pending
                        ? <span className="inline-flex items-center gap-1.5 text-text-muted"><Loader2 size={14} className="animate-spin" /> Thinking…</span>
                        : m.text}
                      {/* T-447 phase 3 — the answer names a screen, so offer to
                          open it. Only routes that genuinely exist are linked
                          (see lib/appRoutes.ts); anything else stays as text. */}
                      {m.role === "assistant" && !m.pending && m.actions && m.actions.length > 0 && (
                        <div className="mt-2.5 pt-2 border-t border-line/60 space-y-2">
                          {m.actions.map(a => (
                            <div key={a.action_id}
                              className={`rounded-lg border px-3 py-2 ${
                                a.state === "executed" ? "border-ok/50 bg-ok/5"
                                : a.state === "failed" ? "border-danger/50 bg-danger/5"
                                : a.state === "cancelled" ? "border-line/60 bg-ink-raised/40 opacity-70"
                                : "border-accent/50 bg-accent/5"}`}>
                              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-muted">
                                <ShieldCheck size={11} className={a.state === "proposed" ? "text-accent" : ""} />
                                {ACTION_LABEL[a.action_type] ?? a.action_type}
                                <span className="ml-auto normal-case tracking-normal">
                                  {a.state === "proposed" ? "waiting for you"
                                    : a.state === "working" ? "applying…"
                                    : a.state === "executed" ? "done"
                                    : a.state === "cancelled" ? "cancelled" : "failed"}
                                </span>
                              </div>
                              <div className="text-[12.5px] text-text mt-1 leading-snug">{a.summary}</div>
                              {a.state === "failed" && a.error && <div className="text-[11px] text-danger mt-1">{a.error}</div>}
                              {a.state === "proposed" && (
                                <div className="mt-2 flex items-center gap-2">
                                  <button type="button" onClick={() => decide(a, "confirm")}
                                    className="inline-flex items-center gap-1 rounded-md bg-accent text-white px-3 py-1.5 text-[12px] font-medium hover:opacity-90">
                                    <Check size={13} /> Confirm
                                  </button>
                                  <button type="button" onClick={() => decide(a, "cancel")}
                                    className="inline-flex items-center gap-1 rounded-md border border-line px-3 py-1.5 text-[12px] text-text-muted hover:text-text">
                                    <Ban size={13} /> Cancel
                                  </button>
                                </div>
                              )}
                              {a.state === "working" && (
                                <div className="mt-2 text-[11px] text-text-muted inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Applying under your login…</div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {m.role === "assistant" && !m.pending && (() => {
                        const routes = extractRoutes(m.text);
                        if (routes.length === 0) return null;
                        return (
                          <div className="mt-2.5 pt-2 border-t border-line/60 flex flex-wrap gap-1.5">
                            {routes.map(r => (
                              <button
                                key={r.path}
                                type="button"
                                onClick={() => { navigate(r.path); setOpen(false); }}
                                title={`Open ${r.label} (${r.path})`}
                                className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-accent/40 text-accent hover:bg-accent/10"
                              >
                                {r.label} <ArrowRight size={11} />
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="border-t border-line p-3" style={{ paddingBottom: "calc(0.75rem + var(--safe-bottom, 0px))" }}>
              {err && <div className="mb-2 text-[11px] text-danger flex items-center gap-1"><AlertTriangle size={12} /> {err}</div>}
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }}
                  placeholder="Ask how to do something, or about a project…"
                  rows={1}
                  className="flex-1 resize-none rounded-lg border border-line bg-ink-card px-3 py-2 text-[13px] text-text placeholder:text-text-dim focus:outline-none focus:border-accent max-h-32"
                />
                <button onClick={() => send(input)} disabled={busy || !input.trim()} className="rounded-lg bg-accent text-white p-2.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center disabled:opacity-40 hover:opacity-90" title="Send" type="button">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
              <div className="mt-1.5 flex items-center justify-between gap-2">
                <p className="text-[10px] text-text-dim">Changes need your Confirm • Enter to send, Shift+Enter for a new line</p>
                {/* T-468 — the moment someone finds a gap is the moment to capture it.
                    Fires the shell's feedback modal by event (importing AppShell here
                    would be circular). */}
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent("coretel:open-feedback"))}
                  className="text-[10px] text-text-muted hover:text-accent whitespace-nowrap"
                  title="Report a problem or suggest an idea"
                >
                  Something wrong? Tell us
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
